import { resolveOptionLabel } from '@/lib/crm/components/properties/booleanOptions';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { getLanguageDisplay } from '@/lib/crm/utils/language';
import { formatPhoneNumber, isPhoneNumberValue } from '@/lib/crm/utils/phone';
import {
  getTimezoneAbbreviation,
  getTimezoneLabel,
} from '@/lib/scheduling/timezone';
import type { CrmObject } from '@/types/crm';

/**
 * The text a property value is searchable by in the overview's "View all
 * values" search: what the row renders, as the user reads it, plus the raw
 * value underneath. Mirrors the value labels' own formatting (option labels
 * for selects and booleans, the formatted phone number, the currency-styled
 * amount) rather than reaching into their DOM, so the search is pure and
 * testable — and so "prosp" finds a select whose stored value is `p`.
 */
export function propertyValueSearchText(
  value: unknown,
  schema: PropertyTypeSchema | undefined
): string[] {
  return collect(value, schema, 0);
}

const MAX_DEPTH = 3;

function collect(
  value: unknown,
  schema: PropertyTypeSchema | undefined,
  depth: number
): string[] {
  if (value === null || value === undefined || depth > MAX_DEPTH) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.flatMap(entry => collect(entry, schema, depth + 1));
  }
  if (typeof value === 'string') {
    return [value, ...renderedStringText(value, schema)];
  }
  if (typeof value === 'number') {
    return [String(value), value.toLocaleString()];
  }
  if (typeof value === 'boolean') {
    return [resolveOptionLabel(schema, String(value))];
  }
  if (isPhoneNumberValue(value)) {
    return [formatPhoneNumber(value)];
  }
  if (typeof value === 'object') {
    if (isQuantity(value)) {
      return quantityText(value);
    }
    return Object.values(value).flatMap(entry =>
      collect(entry, undefined, depth + 1)
    );
  }
  return [];
}

/**
 * What a stored string renders as, when that differs from the string: a
 * select's option label, a timezone's name and abbreviation ("Central Time
 * (US & Canada)", "CDT" for America/Chicago), a language's display name.
 */
function renderedStringText(
  value: string,
  schema: PropertyTypeSchema | undefined
): string[] {
  switch (schema?.field_type_name) {
    case 'timezone': {
      const texts = [getTimezoneLabel(value)];
      try {
        texts.push(getTimezoneAbbreviation(value));
      } catch {
        // Not a valid zone — the label (and the raw value) still search.
      }
      return texts;
    }
    case 'language':
      return [getLanguageDisplay(value)];
    default: {
      const label = resolveOptionLabel(schema, value);
      return label === value ? [] : [label];
    }
  }
}

interface QuantityLike {
  amount: number | string;
  unit?: string | null;
}

function isQuantity(value: object): value is QuantityLike {
  return (
    'amount' in value &&
    (typeof value.amount === 'number' || typeof value.amount === 'string')
  );
}

function quantityText(value: QuantityLike): string[] {
  const amount =
    typeof value.amount === 'string' ? parseFloat(value.amount) : value.amount;
  const texts = [String(value.amount)];
  if (Number.isNaN(amount)) {
    return texts;
  }
  texts.push(amount.toLocaleString());
  if (value.unit) {
    texts.push(value.unit);
    try {
      texts.push(
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: value.unit,
        }).format(amount)
      );
    } catch {
      // Not a currency code (a unit like "hours") — the raw texts suffice.
    }
  }
  return texts;
}

/** The names an association row shows: one per linked record. */
export function associationSearchText(objects: readonly CrmObject[]): string[] {
  return objects.map(object => getDisplayName(object));
}

/**
 * Case-insensitive substring match of a normalized query against any of a
 * row's texts. An empty query matches everything.
 */
export function matchesSearch(
  normalizedQuery: string,
  texts: Iterable<string>
): boolean {
  if (!normalizedQuery) {
    return true;
  }
  for (const text of texts) {
    if (text.toLowerCase().includes(normalizedQuery)) {
      return true;
    }
  }
  return false;
}
